package com.isaias.fyattravel.web.layout

import androidx.compose.runtime.Composable
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.theme.ThemeColors

/**
 * Footer component - compact layout with Get the App at the right corner
 */
@Composable
fun Footer() {
    Div({
        style {
            backgroundColor(ThemeColors.surface)
            color(ThemeColors.textMuted)
            padding(0.5.em, 1.2.em, 0.4.em, 1.2.em)
            width(100.percent)
            property("box-sizing", "border-box")
            property("border-top", "1px solid ${ThemeColors.divider}")
            property("flex-shrink", "0")
            property("@media (max-width: 768px)", "{ padding: 0.9em 1em 0.7em 1em; }")
        }
    }) {
        // 5-column grid: Brand | Quick Links | Top Services | Get the App | Contact
        Div({
            classes(ResponsiveStyles.footerGrid)
            style {
                display(DisplayStyle.Grid)
                gridTemplateColumns("1.8fr 1fr 1fr 1fr 1.8fr")
                gap(0.8.em)
                marginBottom(0.em)
                property("@media (max-width: 1024px)", "{ grid-template-columns: 1fr 1fr; }")
            }
        }) {

            // Column 1: Brand + Social + Copyright
            Div {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(0.5.em)
                        marginBottom(0.75.em)
                    }
                }) {
                    Img(src = "icon_fyat.jpg", attrs = {
                    style {
                        width(28.px)
                        height(28.px)
                        borderRadius(6.px)
                        property("object-fit", "contain")
                    }
                })
                    Div {
                        Div({
                            style {
                                fontWeight("700")
                                color(ThemeColors.textPrimary)
                                fontSize(0.78.em)
                                property("letter-spacing", "0.04em")
                            }
                        }) { Text(WebStrings.t(WebStringKey.FOOTER_BRAND)) }
                    }
                }
                P({
                    style {
                        fontSize(0.68.em)
                        lineHeight("1.4")
                        color(ThemeColors.textMuted)
                        property("max-width", "200px")
                        marginBottom(0.5.em)
                        property("@media (max-width: 760px)", "{ display: none; }")
                    }
                }) {
                    Text(WebStrings.t(WebStringKey.FOOTER_TAGLINE))
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(0.4.em)
                        alignItems(AlignItems.Center)
                        marginBottom(0.5.em)
                    }
                }) {
                    SocialIcon(
                        url = "https://www.facebook.com/share/1By4eWTkce/",
                        svgPath = "M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z",
                        label = WebStrings.t(WebStringKey.FACEBOOK)
                    )
                    SocialIcon(
                        url = "https://www.instagram.com/fyattraveltourltd?stkn=MTEydzJrZjJkY3RzYg==",
                        svgPath = "M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37zm1.5-4.87h.01M7.5 2h9A5.5 5.5 0 0 1 22 7.5v9A5.5 5.5 0 0 1 16.5 22h-9A5.5 5.5 0 0 1 2 16.5v-9A5.5 5.5 0 0 1 7.5 2z",
                        label = WebStrings.t(WebStringKey.INSTAGRAM)
                    )
                    SocialIconX(url = "https://x.com/FyatLtd")
                    SocialIconTikTok(url = "https://tiktok.com/@fyattraveltourltd")
                    SocialIconYouTube(url = "https://youtube.com/@fyattraveltourltd?si=-90SLdAH6GM561Ea")
                }
                
                // WhatsApp direct number (click to open WhatsApp)
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        gap(0.4.em)
                        fontSize(0.72.em)
                        color(ThemeColors.whatsapp)
                        marginBottom(0.4.em)
                        cursor("pointer")
                    }
                    onClick {
                        window.open("https://wa.me/256758250625", "_blank")
                    }
                }) {
                    Span({ style { fontSize(0.9.em) } }) { Text("💬") }
                    Span { Text("+256 758 250 625") }
                }

                // Copyright at bottom of brand column
                P({
                    style {
                        fontSize(0.65.em)
                        color(ThemeColors.textSecondary)
                        margin(0.px)
                        property("@media (max-width: 760px)", "{ display: none; }")
                    }
                }) {
                    Text(WebStrings.t(WebStringKey.COPYRIGHT_TEXT))
                }
            }

            // Column 2: Quick Links
            Div({
                classes(ResponsiveStyles.footerHideOnMobile)
            }) {
                FooterHeading(WebStrings.t(WebStringKey.QUICK_LINKS))
                listOf(
                    WebStrings.t(WebStringKey.SERVICES_LABEL) to "#/categories",
                    WebStrings.t(WebStringKey.ABOUT_US) to "#/",
                    WebStrings.t(WebStringKey.CONTACT_LABEL) to "#/contact",
                    WebStrings.t(WebStringKey.BOOK_NOW_LABEL) to "#/flights"
                ).forEach { (label, url) ->
                    FooterLink(label, url)
                }
            }

            // Column 3: Top Services
            Div({
                classes(ResponsiveStyles.footerHideOnMobile)
            }) {
                FooterHeading(WebStrings.t(WebStringKey.TOP_SERVICES))
                FooterLink(WebStrings.t(WebStringKey.FLIGHT_BOOKING), "#/flights")
                FooterLink(WebStrings.t(WebStringKey.HOTEL_RESERVATIONS), "#/stays")
                FooterLinkAction(WebStrings.t(WebStringKey.VISA_PROCESSING)) {
                    window.location.href = "#/flights?enquiry=true"
                }
                FooterLink(WebStrings.t(WebStringKey.CAR_HIRE), "#/cars")
            }

            // Column 4: Contact
            Div({
                classes(ResponsiveStyles.footerContactCol)
            }) {
                FooterHeading(WebStrings.t(WebStringKey.CONTACT_LABEL))

                // Get the App - RIGHT CORNER
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.FlexEnd)
                        marginBottom(0.5.em)
                        property("@media (max-width: 760px)", "{ align-items: flex-start; }")
                    }
                }) {
                    ContactRow(icon = "📍", text = "Kampala, Uganda", isClickable = true, alignRight = true) {
                        window.open("https://share.google/tbORf5cegPI4ODG3G", "_blank")
                    }
                    ContactRow(icon = "✉", text = "info@fyattraveltour.com", alignRight = true) {
                        window.open("mailto:info@fyattraveltour.com", "_self")
                    }
                    ContactRow(icon = "💬", text = "+256 758 250 625", isClickable = true, alignRight = true) {
                        window.open("https://wa.me/256758250625", "_blank")
                    }
                    ContactRow(icon = "📱", text = "+256 758 249 530", isClickable = true, alignRight = true) {
                        window.open("https://wa.me/256758249530", "_blank")
                    }
                }
            }
            
            // Column 5: Get the App (corner)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.FlexEnd)
                    property("@media (max-width: 760px)", "{ align-items: flex-start; }")
                }
            }) {
                FooterHeading(WebStrings.t(WebStringKey.GET_APP))
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.FlexEnd)
                        gap(0.35.em)
                        property("@media (max-width: 640px)", "{ align-items: flex-start; }")
                    }
                }) {
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(0.4.em)
                            flexWrap(FlexWrap.Wrap)
                            justifyContent(JustifyContent.FlexEnd)
                            property("@media (max-width: 640px)", "{ justify-content: flex-start; }")
                        }
                    }) {
                        AppBadge(
                            label = WebStrings.t(WebStringKey.GET_IT_ON),
                            sublabel = WebStrings.t(WebStringKey.GOOGLE_PLAY),
                            url = "https://play.google.com/store/apps/details?id=com.isaias.fyattravel&pcampaignid=web_share",
                            iconSvg = """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M3.18 23.8c.4.22.85.2 1.24-.02l11.35-6.56-2.56-2.56L3.18 23.8zm-1.43-1.36V1.56c0-.5.28-.93.7-1.15l12.1 12.1-12.1 12.08c-.42-.22-.7-.65-.7-1.15zM21.1 10.3l-2.57-1.49-2.88 2.88 2.88 2.88L21.1 13.7a1.7 1.7 0 000-3.4zM4.42.22c-.39-.22-.84-.2-1.24.02l10.03 10.03 2.56-2.56L4.42.22z"/></svg>""",
                            small = true
                        )
                        AppBadge(
                            label = WebStrings.t(WebStringKey.DOWNLOAD_ON),
                            sublabel = WebStrings.t(WebStringKey.APP_STORE),
                            url = "https://apps.apple.com",
                            iconSvg = """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>""",
                            small = true
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun FooterHeading(text: String) {
    Div({
        style {
            fontWeight("700")
            color(ThemeColors.textPrimary)
            fontSize(0.65.em)
            marginBottom(0.35.em)
            property("letter-spacing", "0.06em")
            property("text-transform", "uppercase")
        }
    }) { Text(text) }
}

@Composable
private fun FooterLink(label: String, url: String) {
    Div({ style { marginBottom(0.3.em) } }) {
        A(href = url, attrs = {
            style {
                color(ThemeColors.textMuted)
                textDecoration("none")
                fontSize(0.7.em)
                property("transition", "color 0.15s ease")
                cursor("pointer")
            }
            onClick {
                it.preventDefault()
                window.location.hash = url.removePrefix("#")
            }
        }) { Text(label) }
    }
}

@Composable
private fun FooterLinkAction(label: String, onClick: () -> Unit) {
    Div({ style { marginBottom(0.25.em) } }) {
        Span({
            style {
                color(ThemeColors.primary)
                textDecoration("underline")
                fontSize(0.7.em)
                cursor("pointer")
                property("transition", "color 0.15s ease")
            }
            onClick { onClick() }
        }) { Text(label) }
    }
}

@Composable
private fun ContactRow(
    icon: String,
    text: String,
    isClickable: Boolean = false,
    alignRight: Boolean = false,
    onClick: (() -> Unit)? = null
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.35.em)
            marginBottom(0.4.em)
            fontSize(0.75.em)
            color(if (isClickable) ThemeColors.primary else ThemeColors.textMuted)
            if (alignRight) justifyContent(JustifyContent.FlexEnd) else justifyContent(JustifyContent.FlexStart)
            if (isClickable || onClick != null) cursor("pointer") else cursor("default")
            width(100.percent)
        }
        if (onClick != null) {
            onClick { onClick() }
        }
    }) {
        Span({ style { fontSize(0.85.em) } }) { Text(icon) }
        if (isClickable) {
            Span({
                style {
                    textDecoration("underline")
                    color(ThemeColors.primary)
                }
            }) { Text(text) }
        } else {
            Text(text)
        }
    }
}

@Composable
private fun SocialIcon(url: String, svgPath: String, label: String) {
    Div({
        style {
            width(24.px)
            height(24.px)
            borderRadius(50.percent)
            backgroundColor(ThemeColors.background)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor("pointer")
            property("transition", "background-color 0.2s ease")
            property("flex-shrink", "0")
        }
        onClick { window.open(url, "_blank") }
        title(label)
        ref { el ->
            el.innerHTML = """<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="$svgPath"/></svg>"""
            onDispose {}
        }
    }) {}
}

@Composable
private fun SocialIconX(url: String) {
    Div({
        style {
            width(24.px)
            height(24.px)
            borderRadius(50.percent)
            backgroundColor(ThemeColors.background)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor("pointer")
            property("transition", "background-color 0.2s ease")
            property("flex-shrink", "0")
        }
        onClick { window.open(url, "_blank") }
        title("X (Twitter)")
        ref { el ->
            el.innerHTML = """<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.742l7.73-8.835L1.254 2.25H8.08l4.253 5.622 5.91-5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>"""
            onDispose {}
        }
    }) {}
}

@Composable
private fun SocialIconTikTok(url: String) {
    Div({
        style {
            width(24.px)
            height(24.px)
            borderRadius(50.percent)
            backgroundColor(ThemeColors.background)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor("pointer")
            property("transition", "background-color 0.2s ease")
            property("flex-shrink", "0")
        }
        onClick { window.open(url, "_blank") }
        title("TikTok")
        ref { el ->
            el.innerHTML = """<svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.79 1.53V6.77a4.85 4.85 0 0 1-1.02-.08z"/></svg>"""
            onDispose {}
        }
    }) {}
}

@Composable
private fun SocialIconYouTube(url: String) {
    Div({
        style {
            width(24.px)
            height(24.px)
            borderRadius(50.percent)
            backgroundColor(ThemeColors.background)
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            cursor("pointer")
            property("transition", "background-color 0.2s ease")
            property("flex-shrink", "0")
        }
        onClick { window.open(url, "_blank") }
        title("YouTube")
        ref { el ->
            el.innerHTML = """<svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.8zM9.6 15.6V8.4l6.3 3.6z"/></svg>"""
            onDispose {}
        }
    }) {}
}

@Composable
private fun AppBadge(label: String, sublabel: String, url: String, iconSvg: String, small: Boolean = false) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(0.4.em)
            backgroundColor(ThemeColors.background)
            color(ThemeColors.textPrimary)
            padding(if (small) 0.3.em else 0.45.em, if (small) 0.65.em else 0.9.em)
            borderRadius(0.4.em)
            cursor("pointer")
            property("transition", "background-color 0.2s ease")
            property("min-width", if (small) "100px" else "120px")
        }
        onClick { window.open(url, "_blank") }
        ref { el ->
            el.querySelector("div.icon-slot")?.let {}
            onDispose {}
        }
    }) {
        Span({
            ref { el ->
                el.innerHTML = iconSvg
                onDispose {}
            }
        }) {}
        Div {
            Div({
                style {
                    fontSize(if (small) 0.55.em else 0.6.em)
                    color(ThemeColors.textSecondary)
                    lineHeight("1")
                }
            }) { Text(label) }
            Div({
                style {
                    fontSize(if (small) 0.7.em else 0.8.em)
                    fontWeight("700")
                    lineHeight("1.3")
                }
            }) { Text(sublabel) }
        }
    }
}
